const chapter = {
  id: 'thermodynamics',
  slug: 'thermodynamics',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Thermodynamics & Kinetic Theory',
        hindi: 'ऊष्मागतिकी और गतिज सिद्धान्त',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '3–4 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 3-4 प्रश्न',
  },
  syllabusTags: ['Thermal Physics', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'thermal-exp',
      title: {
        english: 'Thermal Expansion, Heat Capacity & Calorimetry',
        hindi: 'ऊष्मीय प्रसार, ऊष्माधारिता और कैलोरीमिति',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Linear: $\\Delta L = \\alpha L\\,\\Delta T$. Areal: $\\beta = 2\\alpha$. Volumetric: $\\gamma = 3\\alpha$.',
        hindi: 'रैखिक: $\\Delta L = \\alpha L\\,\\Delta T$. क्षेत्र: $\\beta = 2\\alpha$. वॉल्यूमेट्रिक: $\\gamma = 3\\alpha$.',
            },
            {
              english: 'Anomalous expansion of water: max density at 4°C.',
        hindi: 'पानी का असामान्य विस्तार: अधिकतम घनत्व 4°C पर।',
            },
            {
              english: '**Heat** $Q = m c \\Delta T$, $c$ = specific heat (J/kg·K). **Latent heat** $Q = mL$ during phase change at constant $T$.',
        hindi: '**ऊष्मा** $Q = m c \\Delta T$, $c$ = विशिष्ट ऊष्मा (J/kg·K)। **स्थिर $T$ पर चरण परिवर्तन के दौरान गुप्त ऊष्मा** $Q = mL$।',
            },
            {
              english: '**Calorimetry**: heat lost = heat gained (no losses).',
        hindi: '**कैलोरीमेट्री**: नष्ट हुई ऊष्मा = प्राप्त हुई ऊष्मा (कोई हानि नहीं)।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: '50 g ice at 0°C is mixed with 100 g water at 60°C. Final temperature? ($L_f = 80$ cal/g, $c_w = 1$)',
        hindi: '0°C पर 50 ग्राम बर्फ को 60°C पर 100 ग्राम पानी के साथ मिलाया जाता है। अंतिम तापमान? ($L_f = 80$ cal/g, $c_w = 1$)',
          },
          solution: {
            english: 'Heat to melt ice: $50 \\cdot 80 = 4000$ cal. Heat available from water cooled to $0°$C: $100 \\cdot 60 = 6000$. After melt, $2000$ cal warms $150$ g water by $T$: $T = 2000/150 \\approx 13.3°$C.',
        hindi: 'बर्फ पिघलाने के लिए ताप: $50 \\cdot 80 = 4000$ cal. $0°$C: $100 \\cdot 60 = 6000$ तक ठंडा किए गए पानी से गर्मी उपलब्ध होती है। पिघलने के बाद, $2000$ cal $150$ ग्राम पानी को $T$: $T = 2000/150 \\approx 13.3°$C तक गर्म करता है।',
          },
        },
      ],
    },
    {
      id: 'heat-transfer',
      title: {
        english: 'Heat Transfer — Conduction, Convection, Radiation',
        hindi: 'ऊष्मा स्थानान्तरण — चालन, संवहन, विकिरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Conduction**: $\\dfrac{dQ}{dt} = -kA\\dfrac{dT}{dx}$. Rods in series: $R = \\sum L_i/(k_i A)$. Parallel: $1/R = \\sum k_i A_i / L_i$.',
        hindi: '**चालन**: $\\dfrac{dQ}{dt} = -kA\\dfrac{dT}{dx}$. श्रृंखला में छड़ें: $R = \\sum L_i/(k_i A)$। समानांतर: $1/R = \\sum k_i A_i / L_i$.',
            },
            {
              english: '**Convection**: bulk transport in fluids (natural / forced).',
        hindi: '**संवहन**: तरल पदार्थों में थोक परिवहन (प्राकृतिक/मजबूर)।',
            },
            {
              english: '**Radiation**: emits at all $T > 0$. **Stefan-Boltzmann**: $P = e\\sigma A T^4$. **Wien\'s law**: $\\lambda_{\\max} T = b = 2.9{\\times}10^{-3}$ m·K.',
        hindi: '**विकिरण**: बिल्कुल $T > 0$ उत्सर्जित करता है। **स्टीफ़न-बोल्ट्ज़मैन**: $P = e\\sigma A T^4$। **वीन का नियम**: $\\lambda_{\\max} T = b = 2.9{\\times}10^{-3}$ m·K.',
            },
            {
              english: '**Newton\'s law of cooling**: $dT/dt = -k(T - T_0)$ for small temperature differences.',
        hindi: '**न्यूटन का शीतलन नियम**: छोटे तापमान अंतर के लिए $dT/dt = -k(T - T_0)$।',
            },
          ],
        },
      ],
    },
    {
      id: 'ktg',
      title: {
        english: 'Kinetic Theory of Gases',
        hindi: 'गैसों का गतिज सिद्धान्त',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Pressure of an ideal gas',
        hindi: 'एक आदर्श गैस का दबाव',
          },
          statement: {
            english: '$P = \\dfrac{1}{3}\\dfrac{N m \\langle v^2\\rangle}{V} = \\dfrac{1}{3}\\rho \\langle v^2\\rangle$.',
        hindi: '$P = \\dfrac{1}{3}\\dfrac{N m \\langle v^2\\rangel}{V} = \\dfrac{1}{3}\\rho \\langel v^2\\rangel$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Ideal gas equation**: $PV = nRT = Nk_B T$. $R = 8.314$ J/(mol·K), $k_B = R/N_A$.',
        hindi: '**आदर्श गैस समीकरण**: $PV = nRT = Nk_B T$. $R = 8.314$ J/(mol·K), $k_B = R/N_A$।',
            },
            {
              english: '**Average translational KE per molecule**: $\\langle\\epsilon\\rangle = \\tfrac32 k_B T$.',
        hindi: '**प्रति अणु औसत ट्रांसलेशनल KE**: $\\langle\\epsilon\\rangle = \\tfrac32 k_B T$।',
            },
            {
              english: '**RMS speed**: $v_{\\text{rms}} = \\sqrt{3RT/M} = \\sqrt{3k_B T/m}$. Mean speed $\\bar v = \\sqrt{8RT/(\\pi M)}$. Most probable $v_p = \\sqrt{2RT/M}$. Ratio $v_p : \\bar v : v_{\\text{rms}} = \\sqrt 2 : \\sqrt{8/\\pi} : \\sqrt 3$.',
        hindi: '**RMS गति**: $v_{\\text{rms}} = \\sqrt{3RT/M} = \\sqrt{3k_B T/m}$. माध्य गति $\\bar v = \\sqrt{8RT/(\\pi M)}$. सर्वाधिक संभावित $v_p = \\sqrt{2RT/M}$. अनुपात $v_p: \\bar v: v_{\\text{rms}} = \\sqrt 2: \\sqrt{8/\\pi}: \\sqrt 3$.',
            },
            {
              english: '**Equipartition**: each quadratic DOF carries $\\tfrac12 k_B T$ on average.',
        hindi: '**समविभाजन**: प्रत्येक द्विघात DOF में औसतन $\\tfrac12 k_B T$ होता है।',
            },
            {
              english: '**Mean free path**: $\\lambda = 1/(\\sqrt 2 \\pi d^2 n)$.',
        hindi: '**माध्य मुक्त पथ**: $\\lambda = 1/(\\sqrt 2 \\pi d^2 n)$।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Gas type', 'DOF $f$', '$C_v$', '$C_p$', '$\\gamma = C_p/C_v$'],
          rows: [
            ['Monatomic', '3', '$\\tfrac32 R$', '$\\tfrac52 R$', '$5/3$'],
            ['Diatomic (rigid)', '5', '$\\tfrac52 R$', '$\\tfrac72 R$', '$7/5$'],
            ['Diatomic (vibrating)', '7', '$\\tfrac72 R$', '$\\tfrac92 R$', '$9/7$'],
            ['Polyatomic (non-linear)', '6', '$3R$', '$4R$', '$4/3$'],
          ],
        },
      ],
    },
    {
      id: 'first-law',
      title: {
        english: 'First Law of Thermodynamics',
        hindi: 'ऊष्मागतिकी का प्रथम नियम',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'First law',
        hindi: 'पहला कानून',
          },
          statement: {
            english: '$dQ = dU + dW$ (heat added to system = change in internal energy + work done **by** the system). Sign conv (Indian textbook): $W$ done **by** gas is positive when gas expands.',
        hindi: '$dQ = dU + dW$ (system = change in internal ऊर्जा + कार्य done **by** the system) में गर्मी जोड़ी गई। संकेत रूपांतरण (भारतीय पाठ्यपुस्तक): $W$ **द्वारा** किया गया गैस सकारात्मक है जब गैस फैलती है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Internal energy of ideal gas depends only on $T$: $U = nC_v T$ (referenced).',
        hindi: 'आदर्श गैस की आंतरिक ऊर्जा केवल $T$: $U = nC_v T$ (संदर्भित) पर निर्भर करती है।',
            },
            {
              english: 'Work done in process: $W = \\int P\\,dV$ (area under $P$-$V$ curve).',
        hindi: 'प्रक्रिया में किया गया कार्य: $W = \\int P\\,dV$ ($P$-$V$ वक्र के अंतर्गत क्षेत्र)।',
            },
            {
              english: '$C_p - C_v = R$ (Mayer\'s relation, per mole).',
        hindi: '$C_p - C_v = R$ (मेयर का संबंध, प्रति मोल)।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Process', 'Constant', '$Q$', '$\\Delta U$', '$W$'],
          rows: [
            ['Isothermal', '$T$', '$nRT\\ln(V_2/V_1)$', '$0$', '$nRT\\ln(V_2/V_1)$'],
            ['Isobaric', '$P$', '$nC_p\\,\\Delta T$', '$nC_v\\,\\Delta T$', '$P\\,\\Delta V = nR\\,\\Delta T$'],
            ['Isochoric', '$V$', '$nC_v\\,\\Delta T$', '$nC_v\\,\\Delta T$', '$0$'],
            ['Adiabatic', '$Q = 0$', '$0$', '$nC_v\\,\\Delta T$', '$-\\Delta U$'],
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Adiabatic process (ideal gas)',
        hindi: 'रुद्धोष्म प्रक्रिया (आदर्श गैस)',
          },
          statement: {
            english: '$PV^\\gamma = $ const, $TV^{\\gamma-1} = $ const, $T^\\gamma P^{1-\\gamma} = $ const. Work done **by** gas: $W = \\dfrac{P_1 V_1 - P_2 V_2}{\\gamma - 1} = \\dfrac{nR(T_1 - T_2)}{\\gamma - 1}$.',
        hindi: '$PV^\\gamma = $ स्थिरांक, $TV^{\\gamma-1} = $ स्थिरांक, $T^\\gamma P^{1-\\gamma} = $ स्थिरांक. **गैस द्वारा** किया गया कार्य: $W = \\dfrac{P_1 V_1 - P_2 V_2}{\\gamma - 1} = \\dfrac{nR(T_1 - T_2)}{\\gamma - 1}$।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Find work done by 2 mol ideal gas in isothermal expansion at 300 K from $V$ to $3V$. ($R = 8.314$)',
        hindi: '$V$ से $3V$ तक 300 K पर इज़ोटेर्मल विस्तार में 2 मोल आदर्श गैस द्वारा किया गया कार्य ज्ञात कीजिए। ($R = 8.314$)',
          },
          solution: {
            english: '$W = nRT\\ln 3 = 2 \\cdot 8.314 \\cdot 300 \\cdot 1.0986 \\approx 5481$ J.',
        hindi: '$W = nRT\\ln 3 = 2 \\cdot 8.314 \\cdot 300 \\cdot 1.0986 \\approx 5481$ जे.',
          },
        },
      ],
    },
    {
      id: 'second-law',
      title: {
        english: 'Second Law, Heat Engines & Entropy',
        hindi: 'द्वितीय नियम, ऊष्मा इंजन और एन्ट्रॉपी',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Heat engine',
        hindi: 'इंजन गर्म करें',
          },
          body: {
            english: 'Cyclic device taking heat $Q_H$ from hot reservoir at $T_H$, doing work $W$, and rejecting $Q_C$ to cold reservoir at $T_C$. Efficiency $\\eta = W/Q_H = 1 - Q_C/Q_H$.',
        hindi: 'चक्रीय उपकरण $T_H$ पर गर्म जलाशय से गर्मी $Q_H$ लेता है, कार्य $W$ करता है, और $T_C$ पर ठंडे जलाशय में $Q_C$ को अस्वीकार करता है। दक्षता $\\eta = W/Q_H = 1 - Q_C/Q_H$.',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Carnot\'s theorem',
        hindi: 'कार्नोट का प्रमेय',
          },
          statement: {
            english: 'No engine working between two temperatures can be more efficient than a Carnot (reversible) engine: $\\eta_{\\text{Carnot}} = 1 - T_C/T_H$ (with $T$ in kelvin).',
        hindi: 'दो तापमानों के बीच काम करने वाला कोई भी इंजन कार्नोट (प्रतिवर्ती) इंजन से अधिक कुशल नहीं हो सकता: $\\eta_{\\text{Carnot}} = 1 - T_C/T_H$ (केल्विन में $T$ के साथ)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Refrigerator/heat pump**: COP = $Q_C/W$ (refrigerator); $Q_H/W$ (heat pump). Carnot COP$_R = T_C/(T_H - T_C)$.',
        hindi: '**रेफ्रिजरेटर/हीट पंप**: COP = $Q_C/W$ (रेफ्रिजरेटर); $Q_H/W$ (हीट पंप)। कार्नोट COP$_R = T_C/(T_H - T_C)$.',
            },
            {
              english: '**Second law (Kelvin–Planck)**: no process whose sole result is conversion of heat from a single reservoir into work.',
        hindi: '**दूसरा नियम (केल्विन-प्लैंक)**: ऐसी कोई प्रक्रिया नहीं जिसका एकमात्र परिणाम एकल भंडार से गर्मी को कार्य में परिवर्तित करना हो।',
            },
            {
              english: '**Clausius statement**: heat cannot spontaneously flow from cold to hot.',
        hindi: '**क्लॉसियस का कथन**: ऊष्मा स्वतः ही ठंडे से गर्म की ओर प्रवाहित नहीं हो सकती।',
            },
            {
              english: '**Entropy**: $dS = dQ_{\\text{rev}}/T$. For an isolated system, $\\Delta S \\ge 0$.',
        hindi: '**एंट्रॉपी**: $dS = dQ_{\\text{rev}}/T$. एक पृथक प्रणाली के लिए, $\\Delta S \\ge 0$।',
            },
          ],
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Carnot engine works between 600 K and 300 K. Efficiency?',
        hindi: 'कार्नोट इंजन 600 K और 300 K के बीच काम करता है। दक्षता?',
          },
          solution: {
            english: '$\\eta = 1 - 300/600 = 0.5$ (50%).',
        hindi: '$\\eta = 1 - 300/600 = 0.5$ (50%)।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'PV = nRT',
      desc: {
        english: 'Ideal gas law',
        hindi: 'आदर्श गैस कानून',
      },
    },
    {
      latex: 'v_{\\text{rms}} = \\sqrt{3RT/M}',
      desc: {
        english: 'RMS speed',
        hindi: 'आरएमएस गति',
      },
    },
    {
      latex: 'C_p - C_v = R,\\ \\gamma = C_p/C_v',
      desc: {
        english: 'Mayer + ratio',
        hindi: 'मेयर + अनुपात',
      },
    },
    {
      latex: 'dQ = dU + dW',
      desc: {
        english: 'First law',
        hindi: 'पहला कानून',
      },
    },
    {
      latex: 'PV^\\gamma = \\text{const}',
      desc: {
        english: 'Adiabatic',
        hindi: 'रुद्धोष्म',
      },
    },
    {
      latex: '\\eta_{\\text{Carnot}} = 1 - T_C/T_H',
      desc: {
        english: 'Carnot efficiency',
        hindi: 'कार्नोट दक्षता',
      },
    },
    {
      latex: 'P = e\\sigma A T^4',
      desc: {
        english: 'Stefan-Boltzmann',
        hindi: 'स्टीफन-बोल्ट्ज़मैन',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Adiabatic',
      problem: {
        english: 'A monatomic ideal gas ($\\gamma = 5/3$) at 27°C is compressed adiabatically to 1/8 of its volume. Final temperature?',
        hindi: '27°C पर एक मोनोआटोमिक आदर्श गैस ($\\gamma = 5/3$) को उसके आयतन के 1/8 तक रुद्धोष्म रूप से संपीड़ित किया जाता है। अंतिम तापमान?',
      },
      answer: {
        english: '$T_2 = 1200$ K = $927°$C',
        hindi: '$T_2 = 1200$ K = $927°$C',
      },
      solution: {
        english: '$T_1 V_1^{\\gamma-1} = T_2 V_2^{\\gamma-1} \\Rightarrow T_2 = 300 \\cdot 8^{2/3} = 300 \\cdot 4 = 1200$ K.',
        hindi: '$T_1 V_1^{\\gamma-1} = T_2 V_2^{\\gamma-1} \\Rightarrow T_2 = 300 \\cdot 8^{2/3} = 300 \\cdot 4 = 1200$ K.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'KTG',
      problem: {
        english: 'Ratio of $v_{\\text{rms}}$ of $H_2$ to $O_2$ at the same temperature.',
        hindi: 'एक ही तापमान पर $v_{\\text{rms}}$ का $H_2$ से $O_2$ का अनुपात।',
      },
      answer: {
        english: '$4 : 1$',
        hindi: '$4 : 1$',
      },
      solution: {
        english: '$v_{\\text{rms}} \\propto 1/\\sqrt M$. $\\sqrt{32/2} = 4$.',
        hindi: '$v_{\\text{rms}} \\propto 1/\\sqrt M$. $\\sqrt{32/2} = 4$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Carnot',
      problem: {
        english: 'A Carnot engine has efficiency 40% with sink at 300 K. To raise efficiency to 50% with same sink, source temp must be?',
        hindi: 'एक कार्नोट इंजन की दक्षता 300 K पर सिंक के साथ 40% है। समान सिंक के साथ दक्षता 50% तक बढ़ाने के लिए, स्रोत तापमान कितना होना चाहिए?',
      },
      answer: {
        english: '$600$ K',
        hindi: '$600$ K',
      },
      solution: {
        english: '$1 - 300/T = 0.5 \\Rightarrow T = 600$ K.',
        hindi: '$1 - 300/टी = 0.5 \\Rightarrow टी = 600$ के.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Calorimetry',
      problem: {
        english: '10 g steam at 100°C condenses on 100 g water at 20°C in calorimeter. Final temperature? ($L_v = 540$ cal/g, $c_w = 1$)',
        hindi: 'कैलोरीमीटर में 100°C पर 10 ग्राम भाप 20°C पर 100 ग्राम पानी पर संघनित होती है। अंतिम तापमान? ($L_v = 540$ cal/g, $c_w = 1$)',
      },
      answer: {
        english: '$\\approx 73.6°$C',
        hindi: '$\\approx 73.6°$C',
      },
      solution: {
        english: 'Heat from condensation+cooling: $10(540) + 10(100 - T) = 100(T - 20)$. Solve.',
        hindi: 'संघनन से ऊष्मा+शीतलन: $10(540) + 10(100 - T) = 100(T - 20)$। हल करना।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Wien',
      problem: {
        english: 'A black body emits maximum at 500 nm at 5800 K. At 2900 K, peak wavelength?',
        hindi: 'एक काला पिंड 5800 K पर अधिकतम 500 एनएम उत्सर्जित करता है। 2900 K पर, शिखर तरंगदैर्घ्य?',
      },
      answer: {
        english: '$1000$ nm',
        hindi: '$1000$ एनएम',
      },
      solution: {
        english: '$\\lambda_{\\max} T = $ const, so $\\lambda \\propto 1/T$.',
        hindi: '$\\lambda_{\\max} T = $ स्थिरांक, इसलिए $\\lambda \\propto 1/T$।',
      },
    },
  ],
  next: {
    slug: 'oscillations-waves',
    title: 'Oscillations & Waves',
  },
};

export default chapter;
